import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailWorkMethod,
  InsertHrailWorkMethodBody,
  UpdateHrailWorkMethodNewImageBody,
  UpdateHrailWorkMethodRemoveImageBody,
} from "./types/workMethods";

export const useHrailWorkMethodsApi = () => {
  const api = useApi();

  const getAdminHrailWorkMethod = useCallback(
    (workMethodId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/work_methods/${workMethodId}`;
      return useSWRMutation<HrailWorkMethod, CustomError, string>(url, (url) =>
        api.get<HrailWorkMethod>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailWorkMethods = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/work_methods`, query);
      return useSWRImmutable<Paginated<HrailWorkMethod>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailWorkMethod>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailWorkMethods = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/work_methods`, query);
      return api.get<Paginated<HrailWorkMethod>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailWorkMethod = useCallback(
    async (body: InsertHrailWorkMethodBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailWorkMethodBody>(`/admin/settings/hrail/work_methods`, body);
      mutateCache([`/admin/settings/hrail/work_methods`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailWorkMethod = useCallback(
    async (
      workMethodId: string,
      body: UpdateHrailWorkMethodRemoveImageBody | UpdateHrailWorkMethodNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/hrail/work_methods/${workMethodId}`;
      await api.put<UpdateHrailWorkMethodRemoveImageBody | UpdateHrailWorkMethodNewImageBody>(url, body);
      mutateCache([`/admin/settings/hrail/work_methods`], url);
    },
    [api],
  );

  const updateAdminHrailWorkMethodVisibility = useCallback(
    async (workMethodId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/work_methods/${workMethodId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/work_methods`]);
    },
    [api],
  );

  const updateAdminHrailWorkMethodOrder = useCallback(
    async (workMethodId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/work_methods/${workMethodId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/work_methods`]);
    },
    [api],
  );

  const deleteAdminHrailWorkMethod = useCallback(
    async (workMethodId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/work_methods/${workMethodId}`);
      mutateCache([`/admin/settings/hrail/work_methods`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (workMethodId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/hrail/work_methods/${workMethodId}/image/generate_upload_url?file_name=${encodedFileName}`;
      const uploadUrl = await api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
      mutateCache([`/admin/settings/hrail/work_methods`]);
      return uploadUrl;
    },
    [api],
  );

  return {
    getAdminHrailWorkMethod,
    useAdminHrailWorkMethods,
    getAdminHrailWorkMethods,
    insertAdminHrailWorkMethod,
    updateAdminHrailWorkMethod,
    updateAdminHrailWorkMethodVisibility,
    updateAdminHrailWorkMethodOrder,
    deleteAdminHrailWorkMethod,
    generateUploadImageUrl,
  };
};
